// filepath: c:\Users\barto\Desktop\invayt\web-app\src\features\events\components\EventCard.tsx
import React, { useState } from 'react';
import { GitBranch } from 'lucide-react';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import redirectTo from '../../../utils/redirect';
import { slugify } from '../../../utils/slugify';
import { CATEGORIES } from '../../../constants/categories';
import { AddToCalendarModal } from '../../events/components/AddToCalendarModal';

export interface EventCardProps {
  id: string;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  organizer: string;
  organizerId: string // Nuevo campo para el organizador
  category: any;
  image: string;
  organizerAvatar: string;
  parent_event_id?: string | null;
  onClick?: () => void;
}

export const EventCard: React.FC<EventCardProps> = ({
  id,
  title,
  description,
  date,
  time,
  location,
  organizer, // Nuevo campo
  category,
  organizerAvatar,
  organizerId,
  image,
  parent_event_id,
  onClick
}) => {
  const slug = slugify(title);
  const categoryDetails = CATEGORIES.find(cat => cat.name === category);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Función para truncar texto
  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Función para formatear la fecha al formato esperado por el modal
  const formatEventData = () => {
    return {
      id,
      title,
      description,
      startDate: date,
      startTime: time,
      endTime: undefined,
      location,
      organizer, // Incluimos el organizador en los datos del evento,
      organizerAvatar,
      organizerId
    };
  };

  const handleCalendarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsModalOpen(true);
  };

  return (
    <>
      <tr className="border-b last:border-b-0 hover:bg-gray-50 cursor-pointer" onClick={onClick}>
        {/* Celda 1: Imagen (miniatura uniforme) */}
        <td className="px-3 py-2 md:p-4 align-middle w-28 md:w-48">
          <div
          style={{backgroundColor: 'white !important'}}
            className="relative w-8 h-8 md:w-16 md:h-16 rounded-lg overflow-hidden bg-primary flex items-center justify-center"
          >
            {image ? (
              <img
                src={image}
                alt={title}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover object-center"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLDivElement | null;
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
            ) : null}
            {/* Fallback (solo se muestra si no hay imagen o falla) */}
            <div
              className="absolute inset-0 hidden items-center justify-center"
              style={{ display: image ? 'none' : 'flex' }}
            >
              <span className="text-white font-semibold text-base drop-shadow select-none">
                {title.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        </td>
        {/* Celda 2: Título y Descripción */}
        <td className="px-3 py-2 md:p-4 align-middle min-w-0">
          <div className="flex items-center gap-1 md:gap-2">
            <h3 className="font-bold text-base md:text-lg text-gray-900 truncate" title={title}>{title}</h3>
            {parent_event_id ? (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary"
                title="Side event"
              >
                <GitBranch className="w-3 h-3" />
                
              </span>
            ) : null}
          </div>

          {/* Descripción + botón en una fila (solo móviles) */}
          <div className="mt-0.5 flex items-start gap-1 md:hidden">
            <p
              className="text-xs text-gray-600 flex-1"
              title={truncateText(description, 40)}
            >
              {truncateText(description, 40)}
            </p>
            <Button
              onClick={(e) => { e.stopPropagation(); handleCalendarClick(e); }}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 shrink-0"
              title="Register for event"
              aria-label="Añadir al calendario"
            >
              <svg width="20" height="20" viewBox="0 0 40 40" fill="none">
                <path d="M8.90045 5.61111H10.75H29.25H31.1005C33.6907 5.61111 34.9851 5.61111 35.9745 6.0592C36.8447 6.45334 37.553 7.08181 37.9964 7.85536C38.5 8.73392 38.5 9.8846 38.5 12.1825V13.8333V31.9296C38.5 34.2275 38.5 35.3765 37.9964 36.2551C37.553 37.0287 36.8447 37.6582 35.9745 38.0524C34.9861 38.5 33.6935 38.5 31.1083 38.5H8.89285C6.30767 38.5 5.01315 38.5 4.02478 38.0524C3.15453 37.6582 2.44751 37.0287 2.00409 36.2551C1.5 35.3757 1.5 34.2251 1.5 31.9226V13.8333V12.1893C1.5 9.88685 1.5 8.73478 2.00409 7.85536C2.44751 7.08181 3.15453 6.45334 4.02478 6.0592C5.01412 5.61111 6.31021 5.61111 8.90045 5.61111Z" fill="#BAAFFF" fillOpacity="0.3"/>
                <path d="M1.5 13.8333H38.5M1.5 13.8333V31.9226C1.5 34.2251 1.5 35.3757 2.00409 36.2551C2.44751 37.0287 3.15453 37.6582 4.02478 38.0524C5.01315 38.5 6.30767 38.5 8.89285 38.5H31.1083C33.6935 38.5 34.9861 38.5 35.9745 38.0524C36.8447 37.6582 37.553 37.0287 37.9964 36.2551C38.5 35.3765 38.5 34.2275 38.5 31.9296V13.8333M1.5 13.8333V12.1893C1.5 9.88685 1.5 8.73478 2.00409 7.85536C2.44751 7.08181 3.15453 6.45334 4.02478 6.0592C5.01412 5.61111 6.31021 5.61111 8.90045 5.61111H10.75M38.5 13.8333V12.1825C38.5 9.8846 38.5 8.73392 37.9964 7.85536C37.553 7.08181 36.8447 6.45334 35.9745 6.0592C34.9851 5.61111 33.6907 5.61111 31.1005 5.61111H29.25M10.75 5.61111H29.25M10.75 5.61111V1.5M29.25 5.61111V1.5M25.7812 26.1667H20M20 26.1667H14.2188M20 26.1667V21.0278M20 26.1667V31.3056" stroke="#8170EF" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Button>
          </div>

          {/* Descripción solo desktop/tablet */}
          <p className="text-sm text-gray-500 mt-1 hidden md:block" title={truncateText(description, 40)}>
            {truncateText(description, 40)}
          </p>
        </td>

        {/* Celda 3: Categoría */}
        <td className="p-4 align-middle w-36 hidden md:table-cell">
          <Badge
            style={{ 
              backgroundColor: categoryDetails?.color,
              color: categoryDetails?.textColor
            }}
            className="whitespace-nowrap max-w-[140px] truncate"
            title={category}
          >
            {category}
          </Badge>
        </td>

        {/* Celda 4: Fecha */}
        <td className="p-4 align-middle w-36 hidden md:table-cell text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            <span>{date}</span>
          </div>
        </td>

        {/* Celda 5: Hora */}
        <td className="p-4 align-middle w-36 hidden lg:table-cell text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span>{time}</span>
          </div>
        </td>

        {/* Celda 6: Ubicación */}
        <td className="p-4 align-middle w-36 hidden lg:table-cell text-sm text-gray-600">
          <div className="flex items-center gap-1">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
            <span>PTM Week</span> {/* Truncamos la dirección */}
          </div>
        </td>

        {/* Celda 7: Organizador */}
        <td className="p-4 align-middle w-36 hidden lg:table-cell text-sm text-gray-600">
          <div className="flex items-center gap-1 select-none min-w-0">
            {organizerAvatar ? <img src={organizerAvatar} alt={organizer} className="w-6 h-6 m-1 rounded-full" /> : null}
            <span className="truncate">{organizer}</span>
          </div>
        </td>

        {/* Celda 8: Botón (solo >= md) */}
        <td className="p-4 align-middle w-20 hidden md:table-cell">
          <Button 
            onClick={handleCalendarClick}
            variant="ghost" 
            size="sm" 
            className="h-10 w-10 p-0"
            title="Register for event"
          >
            <svg width="20" height="20" viewBox="0 0 40 40" fill="none">
              <path d="M8.90045 5.61111H10.75H29.25H31.1005C33.6907 5.61111 34.9851 5.61111 35.9745 6.0592C36.8447 6.45334 37.553 7.08181 37.9964 7.85536C38.5 8.73392 38.5 9.8846 38.5 12.1825V13.8333V31.9296C38.5 34.2275 38.5 35.3765 37.9964 36.2551C37.553 37.0287 36.8447 37.6582 35.9745 38.0524C34.9861 38.5 33.6935 38.5 31.1083 38.5H8.89285C6.30767 38.5 5.01315 38.5 4.02478 38.0524C3.15453 37.6582 2.44751 37.0287 2.00409 36.2551C1.5 35.3757 1.5 34.2251 1.5 31.9226V13.8333V12.1893C1.5 9.88685 1.5 8.73478 2.00409 7.85536C2.44751 7.08181 3.15453 6.45334 4.02478 6.0592C5.01412 5.61111 6.31021 5.61111 8.90045 5.61111Z" fill="#BAAFFF" fillOpacity="0.3"/>
              <path d="M1.5 13.8333H38.5M1.5 13.8333V31.9226C1.5 34.2251 1.5 35.3757 2.00409 36.2551C2.44751 37.0287 3.15453 37.6582 4.02478 38.0524C5.01315 38.5 6.30767 38.5 8.89285 38.5H31.1083C33.6935 38.5 34.9861 38.5 35.9745 38.0524C36.8447 37.6582 37.553 37.0287 37.9964 36.2551C38.5 35.3765 38.5 34.2275 38.5 31.9296V13.8333M1.5 13.8333V12.1893C1.5 9.88685 1.5 8.73478 2.00409 7.85536C2.44751 7.08181 3.15453 6.45334 4.02478 6.0592C5.01412 5.61111 6.31021 5.61111 8.90045 5.61111H10.75M38.5 13.8333V12.1825C38.5 9.8846 38.5 8.73392 37.9964 7.85536C37.553 7.08181 36.8447 6.45334 35.9745 6.0592C34.9851 5.61111 33.6907 5.61111 31.1005 5.61111H29.25M10.75 5.61111H29.25M10.75 5.61111V1.5M29.25 5.61111V1.5M25.7812 26.1667H20M20 26.1667H14.2188M20 26.1667V21.0278M20 26.1667V31.3056" stroke="black" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Button>
        </td>
      </tr>

      {/* Modal de registro */}
      <AddToCalendarModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        eventData={formatEventData()}
      />
    </>
  );
};